/**
 * 对应一级、0级节点
 */

import React, { useState } from 'react';
import { observer, useLocalObservable } from 'mobx-react-lite';
import { HelperCore } from '@/common/dependencies';
import normalIcon from '@/assets/img/mcp_tool_normal.png';
import activeIcon from '@/assets/img/mcp_tool_active.png';
import { SVGPureIcon } from '@/ui';
import ConnectionLineStore from '@/components/chat-history/connection-line/store';
import McpToolsFolder from './tools-folder';
import McpToolsText from './tools-text';
import McpToolsOrder from './tools-order';
import McpToolsParams from './tools-params';
import McpToolsSearch from './tools-search';
import McpToolsBUAction from './tools-bu/action';
import McpToolsBUStatus from './tools-bu/status';
import McpToolsBUActionResult from './tools-bu/action-result';
import globalStore from '@/store/global';
import McpToolsQuery from './tools-query';
import { ToolsStatusLevel1 } from './tools-status';
import { useMemo } from 'react';
import PlanQuestion from '../plan-question';
import PlanSdk from '../plan-sdk'
import ColorContainer from '@/components/color-container';
import { useEffect } from 'react';
import McpTyping from '@/components/ani/mcp-loading/typing';
import { useRef } from 'react';
import FoldTransition from './fold-transition';
import GaoKaoParams from '../gaokao-params';
//import McpToolsPlan from './tools-plan';

const McpToolsItem = observer((props) => {
  const {
    className = '',
    mcpStore,
    item,
    isLast,
    messageItem,
    onRenew,
    handleSend,
  } = props;

  const {
    chatStore,
    panelMessageId,
    currentPanelId,
    isSimpleAgent,
  } = mcpStore;

  const $refContainer = useRef(null);
  const $refChildren = useRef(null);
  const [hiddenChildren, setHiddenChildren] = useState(false);
  // 遍历一级节点，获取二三级节点 ID 
  const ids = useMemo(() => {
    const ids = {};
    if(!item.children?.length){
      return ids;
    }

    const level2Events = item.children;
    ids.firstLevel2Id = level2Events[0].planStepId;
    ids.lastLevel2Id = level2Events[level2Events.length - 1].planStepId;
    level2Events.forEach(level2Event => {
      const level3Events = level2Event.children;
      if(!level3Events?.length){
        return;
      }
      level3Events.forEach(level3Event => {
        ids.lastLevel3Id = level3Event.planStepId;

        if(level3Event.status == "running"){
          ids.lastRunningLevel3Id = level3Event.planStepId;
        }
        else if(level3Event.status == "failed" || level3Event.status == "success"){
          ids.lastCompletedLevel3Id = level3Event.planStepId;
          ids.lastCompletedLevel2Id = level3Event.parentStepId;
        }
      });
    });

    return ids;
  }, [item]);

  const isCurrent = isLast && mcpStore.currentTypingId == item.planStepId && chatStore.isAITyping;
  const hasChildren = !!(item.children?.length);
  // 反问和编辑规划任务 空间由子节点完全控制，不加缩进
  const isPlanConfirm = useMemo(() => {
    try{
      const event = item.children[0].children[0];
      return ["toolNeedConfirm","extraData"].includes(event.type);
    } catch(e){}
    return false;
  }, [item]);
  //分享页案例回放的时候，左侧的模块需要展开
  const isMCPShare = location.pathname.includes('/share/mcp');
  const [folded, setFolded] = useState(!isSimpleAgent && !HelperCore.isPhone() && !isMCPShare);
  const toggleFolded = (event) => {
    event.stopPropagation();
    if(!hasChildren){
      return;
    }
    setFolded(v => !v);
    // 延迟，确保dom已经渲染或移除
    setTimeout(() => {
      const id = `${panelMessageId || ''}_${currentPanelId || ''}`;
      ConnectionLineStore.updatePoint(id);
    }, 0);
  }

  // 大纲节点
  const currentPlanDataUpdateData = useMemo(() => {
    const events = messageItem.events;
    if(!events?.length){
      return null;
    }
    const planDataUpdateList = events.filter(event => event.type == "planDataUpdate");
    const len = planDataUpdateList.length;
    if(!len){
      return null;
    }
    const event = planDataUpdateList[len - 1];
    try{
      return JSON.parse(event.content);
    } catch(e){}

    return null;
  }, [messageItem]);


  const isAITyping = useMemo(() => {
    const isAITyping = (chatStore.isAITyping && chatStore.lastMessage?.msg_id == messageItem.msg_id);
    return isAITyping;
  }, [messageItem, chatStore.isAITyping, chatStore.lastMessage]);

  const agentStatusFailed = useMemo(() => {
    try{
      return messageItem.events.find(event => event.type == "agentStatus").success === false;
    } catch(e){}
    return false;
  }, [messageItem]);

  const childrenStatus = useMemo(() => {
    // 简版智能体
    if(isSimpleAgent){
      return "none";
    }

    // 复杂智能体，以模型数据为准
    if(currentPlanDataUpdateData){
      const planStepId = item.planStepId;
      const event = currentPlanDataUpdateData.find(event => event.planStepId == planStepId);
      if(event){
        const { status } = event;
        switch(status){
          case 'in_progress':{
            if(agentStatusFailed){
              return "failed";
            }
            if(!isAITyping){
              return "none";
            }
            return 'running';
          }
          case 'not_started':{
            if(agentStatusFailed){
              return "failed";
            }
            return 'waiting';
          }
          case 'completed':{
            return 'success';
          }
          // 无此状态
          case 'failed':{
            return 'failed';
          }
          default:{
            HelperCore.log(`一级节点，未命中状态`, planStepId, status);

            break;
          }
        }
      }
    }

    // 未输出的，大纲中的节点
    if(item.stepStatus == "waiting"){
      return "waiting";
    }

    if(!item.children?.length){
      return "none";
    }

    const itemStr = JSON.stringify(item);

    // 输出一部分的，包含大纲中的节点
    if(itemStr.includes("stepStatus")){
      if(agentStatusFailed){
        return "failed";
      }
      if(!isAITyping){
        return "none";
      }

      return "running";
    }
    
    // 部分节点 failed，流程会继续执行
    // if(itemStr.includes('"status":"failed"')){
    //   return "failed";
    // }
    if(itemStr.includes('"status":"running"')){
      if(agentStatusFailed){
        return "failed";
      }
      if(!isAITyping){
        return "none";
      }

      return "running";
    }
    // 无三级节点
    if(!itemStr.includes('"status":')){
      if(!isAITyping){
        return "none";
      }

      return "running";
    }
    return "success";
  }, [item, isAITyping, currentPlanDataUpdateData, isSimpleAgent]);

  const planConfirmRunning = useMemo(() => {
    if(isPlanConfirm && childrenStatus == 'running' && item.displayIndex == 0){
      return 5000;
    }else{
      return 0;
    }
  }, [childrenStatus, isPlanConfirm, item.displayIndex, agentStatusFailed]);

    // 是否右侧选中的节点对应的一级节点
  const isActive = useMemo(() => {
    if(planConfirmRunning) return true;
    if(!mcpStore.panelMessageId || !mcpStore.currentPanelId){
      return false;
    }
    if(messageItem?.msg_id != mcpStore.panelMessageId){
      return false;
    }
    try{
      const result = (item.children || []).some(event => {
        return (event.children || []).some(event => {
          return event.planStepId == mcpStore.currentPanelId;
        })
      });
      return result;
    } catch(e){}
    return false;
  }, [item, messageItem, mcpStore.currentPanelId, mcpStore.panelMessageId, planConfirmRunning]);

  // 自动收起
  // 自测效果不好，前端通过子节点是否有 running 判断一级节点状态，但是否还有子节点前端不知道
  useEffect(() => {
    if(isActive) {
      setFolded(false);
      return;
    }

    // 简版智能体
    if(isSimpleAgent){
      return;
    }
    // 移动端展开收起，跳动明显
    if(HelperCore.isPhone()){
      return;
    }
    if(!currentPlanDataUpdateData && !isPlanConfirm){
      return;
    }

    if(childrenStatus == "running"){
      if(planConfirmRunning){
        //第一次出现反问卡片要滚动到合适的位置，加延时是为了避免enableAutoScroll={true}造成的影响
        setTimeout(() => {
          setFolded(false);
        }, 300);
      }else{
        setFolded(false);
      }
    }
    else if([
      "success",
      "failed",
      "waiting",
    ].includes(childrenStatus)){
      if(!isMCPShare){
        setFolded(true);
      }
    }
  }, [childrenStatus, isPlanConfirm, currentPlanDataUpdateData?.status, isSimpleAgent, isActive, mcpStore.currentPanelId, mcpStore.panelMessageId]);

  const handleCardClick = (event) => {
    // debug
    HelperCore.log(`一级节点 点击`);

    if(folded){
      setFolded(false);
    }

    // 当前节点在本一级节点中
    if(isActive){
      return;
    }

    if(!$refContainer.current){
      return;
    }

    let $targetNode = null;
    const $container = $($refContainer.current);
    // 进行中，选中最后一个
    if(childrenStatus == "running"){
      $targetNode = $container.find('.js-mcptools').last();
    }
    // 其它，选中第一个
    else {
      $targetNode = $container.find('.js-mcptools').first();
    }

    if($targetNode?.length){
      $targetNode.click();
      // setTimeout(() => {
      //   $refContainer.current.scrollIntoView();
      // }, 20);
    }
    else {
      HelperCore.log(`未找到节点`, childrenStatus, event);
    }
  }

  const renderChildren = () => {
    return (
      item.children.map((itemLevel2, indexLevel2) => {
        const {
          type,
        } = itemLevel2;
        
        switch(type){
          
          case "subPlanStep":{
            return (
              <McpToolsFolder 
                key={`${indexLevel2}_${itemLevel2.planStepId || ''}`} 
                className="mt-8px" 
                title={itemLevel2.titleCN || itemLevel2.title || itemLevel2.description} 
                event={itemLevel2} 
                index={indexLevel2}
                mcpStore={mcpStore}
                isAITyping={isAITyping}
                >
                {
                  (itemLevel2.children || []).map((itemLevel3, indexLevel3) => {
                    const {
                      type,
                      text,
                      tool,
                    } = itemLevel3;

                    const className = indexLevel3 == 0 ? "" : "mt-8px";

                    switch(type){
                      case "queryList": 
                        return (
                          <McpToolsOrder
                            key={`${indexLevel3}_${text || ''}`}
                            className={`${className}`}
                            event={itemLevel3}
                            mcpStore={mcpStore}
                            messageItem={messageItem}
                            >
                          </McpToolsOrder>
                        )
                      case "planStatus":
                      case "planThink":{
                        // 已结束的 think 节点
                        if(["success", "failed"].includes(itemLevel3.status) && globalStore.globalConfig.hideFinishedThink){
                          return null;
                        }

                        return (
                          <McpToolsOrder
                            key={`${indexLevel3}_${text || ''}`}
                            className={`${className}`}
                            event={itemLevel3}
                            mcpStore={mcpStore}
                            messageItem={messageItem}
                            isAITyping={isAITyping}
                            >
                            {itemLevel3.status == "running" ? "大模型正在思考，将尽快调用工具执行任务" : "大模型已经思考完毕"}
                          </McpToolsOrder>
                          // <McpToolsText 
                          //   key={`${indexLevel3}_${text || ''}`} 
                          //   className={`${className}`}
                          //   event={itemLevel3}>
                          //   {text}
                          // </McpToolsText>
                        )
                      }
                      case "toolStatus":{
                        if((tool == "bu_status") && itemLevel3.buType == "action"){
                          return (
                            <McpToolsBUAction 
                              key={`${indexLevel3}_toolStatus`}
                              className={`${className}`}
                              event={itemLevel3}
                              mcpStore={mcpStore}
                              messageItem={messageItem}
                              />
                          )
                        }
                        if((tool == "bu_status") && itemLevel3.buType == "result"){
                          return (
                            <McpToolsBUActionResult 
                              key={`${indexLevel3}_toolStatus`}
                              className={`${className}`}
                              event={itemLevel3}
                              mcpStore={mcpStore}
                              messageItem={messageItem}
                              />
                          )
                        }
                        if(tool == "bu_status" && itemLevel3.buType == "status"){
                          return (
                            <McpToolsBUStatus 
                              key={`${indexLevel3}_toolStatus`}
                              className={`${className}`}
                              event={itemLevel3}
                              mcpStore={mcpStore}
                              messageItem={messageItem}
                              />
                          )
                        }

                        if(
                          (tool == "browser_task_search" && itemLevel3.buType == "task_search" ) ||
                          (tool == "browser_task_search" && itemLevel3.buType == "task_add_to_cart" ) ||
                          (tool == "bu_status" && itemLevel3.buType === 'task_status')
                        ){
                          return itemLevel3.buType === 'task_search' || itemLevel3.buType === 'task_add_to_cart' ? (
                            <McpToolsSearch
                              key={`${indexLevel3}_toolStatus`}
                              className={`${className}`}
                              event={itemLevel3}
                              mcpStore={mcpStore}
                              messageItem={messageItem}
                            />
                          ) : null
                        }

                        try{
                          let toolParams = itemLevel3.message.tool_request || itemLevel3.message.tool_respond;
                          if(
                            (
                              !toolParams 
                              || globalStore.cloudConfig.webConfig?.support_show_tool_params != 1
                            )
                            && !itemLevel3.children?.length
                          ){
                            throw new Error("no params");
                          }

                          return (
                            <McpToolsParams
                              key={`${indexLevel3}_toolStatus`}
                              className={`${className}`}
                              event={itemLevel3}
                              mcpStore={mcpStore}
                              messageItem={messageItem}
                              isAITyping={isAITyping}
                              hideTitle={item.hideTitle}
                              >
                            </McpToolsParams>
                          )
                        } catch(e){
                          return (
                            <McpToolsOrder 
                              key={`${indexLevel3}_toolStatus`}
                              className={`${className}`}
                              type={tool} 
                              event={itemLevel3}
                              mcpStore={mcpStore}
                              messageItem={messageItem}
                              isAITyping={isAITyping}
                              >
                              {mcpStore.getFixedToolText(itemLevel3, true)}
                              {
                                !mcpStore.getFixedToolText(itemLevel3).done
                                ? null
                                : <SVGPureIcon name={`done`} className='size-16px ml-6px text-[#33C481]' />
                              }
                            </McpToolsOrder>
                          )
                        }
                      }
                      case "agentBlock":
                      case "agentInteraction":{
                        return (
                          <McpToolsParams
                            key={`${indexLevel3}_toolStatus`}
                            className={`${className}`}
                            event={itemLevel3}
                            mcpStore={mcpStore}
                            messageItem={messageItem}
                            isAITyping={isAITyping}
                            >
                          </McpToolsParams>
                        )
                      }
                      case "planUpdate":{
                        return (
                          <McpToolsOrder 
                            key={`${indexLevel3}_planUpdate`}
                            className={`${className}`}
                            type="sandbox_coder" 
                            event={itemLevel3}
                            mcpStore={mcpStore}
                            messageItem={messageItem}
                            >
                            {mcpStore.getFixedToolText(itemLevel3, true)}
                            {
                              !mcpStore.getFixedToolText(itemLevel3).done
                              ? null
                              : <SVGPureIcon name={`done`} className='size-16px ml-6px text-[#33C481]' />
                            }
                          </McpToolsOrder>
                        )
                      }
                      case "toolNeedConfirm":{
                        if(
                          itemLevel3.status != "running" 
                          || item.displayIndex != 0
                        ){
                          return (
                            <McpToolsParams
                              key={`${indexLevel3}_toolStatus`}
                              className={`${className}`}
                              event={itemLevel3}
                              mcpStore={mcpStore}
                              messageItem={messageItem}
                              isAITyping={isAITyping}
                              >
                            </McpToolsParams>
                          )
                        }

                        return (
                          <div 
                          key={`${indexLevel3}_${itemLevel3.status}_toolNeedConfirm`} 
                          className="w-full px-8px pb-8px">
                            <PlanQuestion
                              scene="left"
                              planData={itemLevel3.detail}
                              planed={itemLevel3.status == 'success' || !chatStore?.isOwner || !messageItem?.is_doing}
                              planStepId={itemLevel3.planStepId}
                              hideTitle={true}
                              requestPlanParams={{
                                id: itemLevel3.planStepId,
                                message_id: itemLevel3.msg_id,
                                conversation_id: itemLevel3.conversation_id,
                              }}
                            />
                          </div>
                        )
                      }
                      case "extraData": {
                        return (
                          <div 
                            key={`${indexLevel3}_${itemLevel3.status}_extraData`} 
                            className="w-full px-8px pb-8px">
                            <GaoKaoParams 
                              event={itemLevel3}
                              mcpStore={mcpStore}
                              messageItem={messageItem}
                              planed={itemLevel3.status == 'success' || !chatStore?.isOwner || !messageItem?.is_doing}
                              isAITyping={isAITyping}
                              handleSend={handleSend} />
                          </div>
                        )
                      }
                    }
                  })
                }
              </McpToolsFolder>
            )
          }
        }
      })
    )
  }

  if(item.hideTitle){
    if(!hasChildren){
      return null;
    }

    return (
      <div 
        className=""
        ref={$refContainer}>
        {renderChildren()}
      </div>
    )
  }
  return (
    <ColorContainer className={["w-full flex flex-col", {'mb-55px' : planConfirmRunning}]} active={isActive}>
      <div 
        id={isActive ? 'mcp-tools-active' : ''}
        className={`mcp-tools ui-tools-box ${isActive ? '' : ' hover:border-black hover:border-opacity-30'} ${isCurrent ? "mcp-tools-current" : ""} ${isLast ? "" : "mcp-tools-done"} relative bg-white ${className}`}
        onClick={handleCardClick}
        ref={$refContainer}
        >
        <div 
          className={`flex items-center select-none w-full ${hasChildren ? 'cursor-pointer' : ''}`}
          >
          <div className="h-24px rounded-full min-w-24px bg-black bg-opacity-6 text-16px font-bold flex items-center justify-center mr-13px">{item.displayIndex + 1}</div>
          <div className="text-container text-16px leading-18px mr-10px flex h-full items-center w-0 flex-grow">
            <div className="font-bold">{item.title || item.description}</div>
          </div>
          <ToolsStatusLevel1 status={childrenStatus} className="mr-16px" />
          <div 
            className={`size-16px transition-transform transition-[0.25s] transform flex items-center justify-center ${folded ? 'rotate-[180deg]' : ''} ${hasChildren ? '' : 'hidden'}`}
            onClick={toggleFolded}>
            <SVGPureIcon className='size-12px' name="mcp_left_card_fold" />
          </div>
        </div>
        {
          hasChildren
          ? (
            <FoldTransition
              beforeGetHeight={() => {
                if(folded) return; 
                setHiddenChildren(false)
              }}
              afterTranstion={() => {
                if(!folded) return; 
                setTimeout(() => {
                  setHiddenChildren(true)
                  setTimeout(() => {
                    const id = `${panelMessageId || ''}_${currentPanelId || ''}`;
                    ConnectionLineStore.updatePoint(id);
                  }, 10);
                }, 100)
              }}
              unfolded={!folded}
              enabled={!isAITyping}
              className={(isAITyping) ? "overflow-visible" : ""}
            >
              <div 
                className={[planConfirmRunning ? '' : 'pl-26px', (hiddenChildren || isAITyping) && folded ? "hidden" : ""]}
                ref={$refChildren}
              >
                <div className="mt-12px"></div>
                {renderChildren()}
              </div>
            </FoldTransition>
          )
          : null
        }
      </div>
    </ColorContainer>
  )
});
export default McpToolsItem;